const SESSION_KEY = "oxiclear_atlas_chat_token_v1";
const OPENING_KEY = "oxiclear_atlas_chat_opening_v1";
const POLL_INTERVAL_MS = 15_000;
const CARE_NAME = "OxiClear Customer Care";
const FALLBACK_OPENING = "Hi, this is OxiClear Customer Care. How can we help?";

/**
 * The storefront entry to OxiClear Customer Care. Quiet until clicked: the
 * launcher never pops up or interrupts. On click, the page context (page,
 * product, cart, air reading, business page) travels with the session so
 * the first line is relevant to where the visitor is, framed as an offer.
 */
class OxiClearAtlasChat extends HTMLElement {
  constructor() {
    super();
    this.token = "";
    this.pollTimer = 0;
    this.busy = false;
    this.messages = [];
    this.opening = "";
    this.starters = [];
    this.cart = null;
  }

  connectedCallback() {
    this.endpoint = this.dataset.endpoint || "";
    this.pagePath = this.dataset.pagePath || window.location.pathname || "/";
    this.launcher = this.querySelector("[data-atlas-launcher]");
    this.panel = this.querySelector("[data-atlas-panel]");
    this.closeButton = this.querySelector("[data-atlas-close]");
    this.messageList = this.querySelector("[data-atlas-messages]");
    this.startersList = this.querySelector("[data-atlas-starters]");
    this.status = this.querySelector("[data-atlas-status]");
    this.form = this.querySelector("[data-atlas-form]");
    this.input = this.querySelector("[data-atlas-input]");
    this.sendButton = this.querySelector("[data-atlas-send]");
    this.website = this.querySelector("[data-atlas-website]");

    if (!this.endpoint || !this.launcher || !this.panel || !this.form || !this.input) {
      return;
    }

    this.token = this.readStored(SESSION_KEY);
    this.restoreOpening();
    this.launcher.addEventListener("click", () => this.open());
    this.closeButton?.addEventListener("click", () => this.close());
    this.form.addEventListener("submit", (event) => this.send(event));
    this.input.addEventListener("input", () => this.resizeComposer());
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.form.requestSubmit();
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.panel.hidden) {
        this.close();
      }
    });
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && !this.panel.hidden && this.token) {
        void this.refreshHistory();
      }
    });

    this.renderMessages();
  }

  disconnectedCallback() {
    this.stopPolling();
  }

  readStored(key) {
    try {
      return window.sessionStorage.getItem(key) || "";
    } catch {
      return "";
    }
  }

  writeStored(key, value) {
    try {
      if (value) {
        window.sessionStorage.setItem(key, value);
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch {
      // The current tab can continue even when storage is unavailable.
    }
  }

  storeToken(token) {
    this.token = token;
    this.writeStored(SESSION_KEY, token);
  }

  forgetToken() {
    this.token = "";
    this.opening = "";
    this.starters = [];
    this.writeStored(SESSION_KEY, "");
    this.writeStored(OPENING_KEY, "");
  }

  restoreOpening() {
    try {
      const stored = JSON.parse(this.readStored(OPENING_KEY) || "null");

      if (stored && typeof stored.opening === "string") {
        this.opening = stored.opening;
        this.starters = Array.isArray(stored.starters) ? stored.starters.slice(0, 2) : [];
      }
    } catch {
      this.opening = "";
      this.starters = [];
    }
  }

  storeOpening(opening, starters) {
    this.opening = typeof opening === "string" ? opening : "";
    this.starters = Array.isArray(starters)
      ? starters.filter((item) => typeof item === "string" && item.trim()).slice(0, 2)
      : [];
    this.writeStored(
      OPENING_KEY,
      this.opening ? JSON.stringify({ opening: this.opening, starters: this.starters }) : "",
    );
  }

  async loadCart() {
    try {
      const response = await fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" });

      if (!response.ok) {
        return;
      }

      const cart = await response.json();

      this.cart = {
        itemCount: Number(cart.item_count) || 0,
        subtotal: `${cart.currency || ""} ${(Number(cart.total_price) || 0) / 100}`.trim(),
        items: (Array.isArray(cart.items) ? cart.items : []).slice(0, 12).map((item) => ({
          title: String(item.product_title || item.title || "").slice(0, 160),
          quantity: Number(item.quantity) || 0,
        })),
      };
    } catch {
      this.cart = null;
    }
  }

  readAirCheck() {
    const indexNode = document.querySelector("[data-air-index]");
    const statusNode = document.querySelector("[data-air-status]");
    const placeNode = document.querySelector("[data-air-place]");

    if (!indexNode) {
      return null;
    }

    const aqi = Number(String(indexNode.textContent || "").replace(/[^\d.]/g, ""));

    if (!Number.isFinite(aqi) || aqi <= 0) {
      return null;
    }

    return {
      aqi,
      band: String(statusNode?.textContent || "").trim().slice(0, 40),
      city: String(placeNode?.textContent || "").trim().slice(0, 80),
    };
  }

  buildContext() {
    const data = this.dataset;
    const context = {
      page: {
        path: window.location.pathname || this.pagePath,
        type: data.pageType || "",
        title: (data.pageTitle || document.title || "").slice(0, 160),
        locale: data.locale || "",
      },
    };

    if (data.productHandle) {
      const descriptor = `${data.productTitle || ""} ${data.productTags || ""}`;
      const valve = /no[\s-]?valve/i.test(descriptor)
        ? "no-valve"
        : /valve/i.test(descriptor)
          ? "valved"
          : "";

      context.product = {
        handle: data.productHandle,
        title: (data.productTitle || "").slice(0, 160),
        variantTitle: (data.productVariant || "").slice(0, 120),
        price: (data.productPrice || "").slice(0, 40),
        available: data.productAvailable === "true",
        tags: (data.productTags || "").split(",").map((tag) => tag.trim()).filter(Boolean).slice(0, 12),
        valve,
        audience: /kid|child/i.test(descriptor) ? "kids" : "adult",
      };
    }

    if (data.business === "true") {
      context.business = { isBusinessPage: true };
    }

    if (data.customer === "true") {
      context.customer = { loggedIn: true };
    }

    const airCheck = this.readAirCheck();

    if (airCheck) {
      context.airCheck = airCheck;
    }

    if (this.cart && this.cart.itemCount > 0) {
      context.cart = this.cart;
    }

    return context;
  }

  async open() {
    this.panel.hidden = false;
    this.launcher.setAttribute("aria-expanded", "true");
    this.launcher.hidden = true;
    this.input.focus({ preventScroll: true });

    await this.loadCart();

    if (this.token) {
      await this.refreshHistory();
    } else {
      await this.startSession();
    }

    this.startPolling();
  }

  close() {
    this.panel.hidden = true;
    this.launcher.hidden = false;
    this.launcher.setAttribute("aria-expanded", "false");
    this.stopPolling();
    this.launcher.focus({ preventScroll: true });
  }

  startPolling() {
    this.stopPolling();
    this.pollTimer = window.setInterval(() => {
      if (!document.hidden && !this.busy && this.token) {
        void this.refreshHistory({ quiet: true });
      }
    }, POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = 0;
    }
  }

  async request(action, payload = {}) {
    const headers = { "Content-Type": "application/json" };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const body = {
      action,
      pagePath: window.location.pathname || this.pagePath,
      website: this.website?.value || "",
      ...payload,
    };

    if (action === "start" || action === "message") {
      body.context = this.buildContext();
    }

    const response = await fetch(this.endpoint, {
      method: "POST",
      mode: "cors",
      cache: "no-store",
      headers,
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      const error = new Error(result.error || "Customer Care chat is temporarily unavailable.");
      error.status = response.status;
      throw error;
    }

    return result.data;
  }

  async startSession() {
    this.setBusy(true, "Connecting...");

    try {
      const data = await this.request("start");
      this.storeToken(data.token);
      this.storeOpening(data.opening, data.starters);
      this.messages = Array.isArray(data.messages) ? data.messages : [];
      this.renderMessages();
      this.setStatus("");
    } catch (error) {
      this.setStatus(error.message, "error");
    } finally {
      this.setBusy(false);
    }
  }

  async refreshHistory(options = {}) {
    if (!this.token) {
      return;
    }

    try {
      const data = await this.request("history");
      this.messages = Array.isArray(data.messages) ? data.messages : [];
      this.renderMessages();

      if (!options.quiet) {
        this.setStatus("");
      }
    } catch (error) {
      if (error.status === 401) {
        this.forgetToken();
        await this.startSession();
        return;
      }

      if (!options.quiet) {
        this.setStatus(error.message, "error");
      }
    }
  }

  askStarter(text) {
    this.input.value = text;
    this.resizeComposer();
    this.form.requestSubmit();
  }

  async send(event) {
    event.preventDefault();

    const message = this.input.value.trim();

    if (message.length < 2 || this.busy) {
      return;
    }

    if (!this.token) {
      await this.startSession();
    }

    if (!this.token) {
      return;
    }

    const optimisticId = `visitor-${Date.now()}`;
    this.messages = [
      ...this.messages,
      {
        id: optimisticId,
        sender: "visitor",
        body: message,
        createdAt: new Date().toISOString(),
      },
    ];
    this.input.value = "";
    this.resizeComposer();
    this.renderMessages({ pending: true });
    this.setBusy(true, "Checking...");

    try {
      const data = await this.request("message", { message });
      this.messages = Array.isArray(data.messages) ? data.messages : [];
      this.renderMessages();
      this.setStatus("");
    } catch (error) {
      this.messages = this.messages.filter((item) => item.id !== optimisticId);
      this.input.value = message;
      this.resizeComposer();
      this.renderMessages();

      if (error.status === 401) {
        this.forgetToken();
      }

      this.setStatus(error.message, "error");
    } finally {
      this.setBusy(false);
      this.input.focus({ preventScroll: true });
    }
  }

  renderStarters() {
    if (!this.startersList) {
      return;
    }

    this.startersList.replaceChildren();

    const show = this.messages.length === 0 && this.starters.length > 0;

    this.startersList.hidden = !show;

    if (!show) {
      return;
    }

    this.starters.forEach((starter) => {
      const button = document.createElement("button");

      button.type = "button";
      button.className = "oc-atlas-chat__starter";
      button.textContent = starter;
      button.addEventListener("click", () => this.askStarter(starter));
      this.startersList.append(button);
    });
  }

  renderMessages(options = {}) {
    if (!this.messageList) {
      return;
    }

    this.messageList.replaceChildren();

    if (this.messages.length === 0) {
      this.messageList.append(
        this.createMessage({
          sender: "atlas",
          body: this.opening || FALLBACK_OPENING,
        }),
      );
    } else {
      this.messages.forEach((message) => {
        this.messageList.append(this.createMessage(message));
      });
    }

    if (options.pending) {
      const typing = document.createElement("div");
      typing.className = "oc-atlas-chat__typing";
      typing.textContent = "Checking";
      this.messageList.append(typing);
    }

    this.renderStarters();

    window.requestAnimationFrame(() => {
      this.messageList.scrollTop = this.messageList.scrollHeight;
    });
  }

  createMessage(message) {
    const sender = ["visitor", "atlas", "support"].includes(message.sender)
      ? message.sender
      : "support";
    const labels = {
      visitor: "You",
      atlas: CARE_NAME,
      support: CARE_NAME,
    };
    const item = document.createElement("article");
    const label = document.createElement("span");
    const body = document.createElement("p");

    item.className = `oc-atlas-chat__message oc-atlas-chat__message--${sender}`;
    label.className = "oc-atlas-chat__message-label";
    label.textContent = labels[sender];
    body.className = "oc-atlas-chat__message-body";
    this.appendMessageBody(body, String(message.body || ""));
    item.append(label, body);

    return item;
  }

  appendMessageBody(container, value) {
    const guideUrl = "https://www.oxiclear.com/pages/how-to-use";
    const parts = value.split(guideUrl);

    parts.forEach((part, index) => {
      container.append(document.createTextNode(part));

      if (index < parts.length - 1) {
        const link = document.createElement("a");
        link.href = guideUrl;
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = "Open the How to Use guide";
        container.append(link);
      }
    });
  }

  setBusy(busy, message = "") {
    this.busy = busy;
    this.input.disabled = busy;
    this.sendButton.disabled = busy;

    if (message) {
      this.setStatus(message);
    }
  }

  setStatus(message, tone = "") {
    this.status.textContent = message || "";

    if (tone) {
      this.status.dataset.tone = tone;
    } else {
      delete this.status.dataset.tone;
    }
  }

  resizeComposer() {
    this.input.style.height = "auto";
    this.input.style.height = `${Math.min(this.input.scrollHeight, 112)}px`;
  }
}

if (!customElements.get("oxiclear-atlas-chat")) {
  customElements.define("oxiclear-atlas-chat", OxiClearAtlasChat);
}
